/* eslint-disable no-unused-vars */
import {WalletPlatform} from '@/enums/WalletPlatform'
import {
  RejectedByUser,
  SdkNotInitializedError,
} from '@/model/wallets/types/WalletErrors'
import {TWalletNetwork} from '@/model/wallets/types/WalletNetwork'
import {NeoXChainHelper, TNeoXChainConfig} from '@/helpers/NeoXChainHelper'
import {ConnectionStrategy} from './ConnectionStrategy'

type MetaMaskEthereumProvider = {
  request: (args: {method: string; params?: unknown[]}) => Promise<unknown>
  on?: (event: string, handler: (...args: unknown[]) => void) => void
  removeListener?: (
    event: string,
    handler: (...args: unknown[]) => void
  ) => void
  isMetaMask?: boolean
  isNEOLine?: boolean
  isNeoLine?: boolean
}

export type MetaMaskProviderContext = {
  type: 'metamask-ethereum'
  provider: MetaMaskEthereumProvider
  address: string | null
}

export const isMetaMaskProvider = (
  provider: unknown
): provider is MetaMaskProviderContext =>
  typeof provider === 'object' &&
  provider !== null &&
  (provider as MetaMaskProviderContext).type === 'metamask-ethereum'

export const METAMASK_DOWNLOAD_URL = 'https://metamask.io/download/'

const METAMASK_RDNS = 'io.metamask'
const EIP6963_ANNOUNCE_EVENT = 'eip6963:announceProvider'
const EIP6963_REQUEST_EVENT = 'eip6963:requestProvider'
const PROVIDER_WAIT_MS = 1500
const USER_REJECTED_CODE = 4001
const UNKNOWN_CHAIN_CODE = 4902

export const WrongMetaMaskNetworkError = (): Error =>
  new Error('MetaMask is not on the expected Neo X network')

const isEip1193Provider = (
  candidate: unknown
): candidate is MetaMaskEthereumProvider =>
  typeof candidate === 'object' &&
  candidate !== null &&
  typeof (candidate as MetaMaskEthereumProvider).request === 'function'

const isMetaMaskFlagged = (
  candidate: unknown
): candidate is MetaMaskEthereumProvider =>
  isEip1193Provider(candidate) &&
  candidate.isMetaMask === true &&
  candidate.isNEOLine !== true &&
  candidate.isNeoLine !== true

const announcedMetaMask = (event: Event): MetaMaskEthereumProvider | null => {
  const {info, provider} = (event as CustomEvent).detail ?? {}

  return info?.rdns === METAMASK_RDNS && isEip1193Provider(provider)
    ? provider
    : null
}

let announcedProvider: MetaMaskEthereumProvider | null = null

if (typeof window !== 'undefined') {
  window.addEventListener(EIP6963_ANNOUNCE_EVENT, event => {
    announcedProvider = announcedMetaMask(event) ?? announcedProvider
  })
  window.dispatchEvent(new Event(EIP6963_REQUEST_EVENT))
}

const injectedMetaMask = (): MetaMaskEthereumProvider | null => {
  const ethereum = (window as any).ethereum
  const nested = Array.isArray(ethereum?.providers)
    ? ethereum.providers.find(isMetaMaskFlagged)
    : undefined

  if (nested) return nested

  return isMetaMaskFlagged(ethereum) ? ethereum : null
}

export const resolveMetaMaskProvider = (): MetaMaskEthereumProvider | null => {
  if (typeof window === 'undefined') return null

  return announcedProvider ?? injectedMetaMask()
}

export const isMetaMaskAvailableInBrowser = () => !!resolveMetaMaskProvider()

const waitForMetaMaskProvider = (): Promise<MetaMaskEthereumProvider | null> => {
  const existing = resolveMetaMaskProvider()

  if (existing) return Promise.resolve(existing)

  return new Promise(resolve => {
    const done = () => {
      clearTimeout(timeoutId)
      window.removeEventListener(EIP6963_ANNOUNCE_EVENT, onAnnounce)
      resolve(resolveMetaMaskProvider())
    }

    const onAnnounce = (event: Event) => {
      if (announcedMetaMask(event)) done()
    }

    const timeoutId = setTimeout(done, PROVIDER_WAIT_MS)

    window.addEventListener(EIP6963_ANNOUNCE_EVENT, onAnnounce)
    window.dispatchEvent(new Event(EIP6963_REQUEST_EVENT))
  })
}

const errorCodeOf = (error: any): unknown =>
  error?.data?.originalError?.code ?? error?.code

const request = async <T>(
  provider: MetaMaskEthereumProvider,
  method: string,
  params?: unknown[]
): Promise<T> => {
  try {
    return (await provider.request({method, params})) as T
  } catch (error) {
    if (errorCodeOf(error) === USER_REJECTED_CODE) throw RejectedByUser

    throw error
  }
}

export class MetaMaskConnection implements ConnectionStrategy {
  private ethProvider: MetaMaskEthereumProvider | null = null
  private address: string | null = null
  private onAnotherNetwork = false
  private detachListeners?: () => void

  constructor(private readonly network: TWalletNetwork) {}

  async init(onDeleteAdapter?: () => void) {
    this.detachListeners?.()
    this.ethProvider = await waitForMetaMaskProvider()

    if (!this.ethProvider) {
      throw SdkNotInitializedError(WalletPlatform.METAMASK)
    }

    this.attachListeners(this.ethProvider, onDeleteAdapter)
  }

  isInstalled() {
    return !!this.ethProvider || isMetaMaskAvailableInBrowser()
  }

  async connect() {
    const provider = this.requireProvider()
    const accounts = await request<string[]>(provider, 'eth_requestAccounts')
    const address = accounts?.[0] ?? null

    if (!address) throw RejectedByUser

    await this.switchToExpectedChain(provider)

    this.address = address
    this.onAnotherNetwork = false

    return this.contextOf(provider)
  }

  getConnectedProvider(): MetaMaskProviderContext | null {
    return this.ethProvider ? this.contextOf(this.ethProvider) : null
  }

  async disconnect() {
    this.address = null
    this.onAnotherNetwork = false
  }

  async ensureNetwork() {
    await this.switchToExpectedChain(this.requireProvider())
  }

  restoreBlockedByNetwork() {
    return this.onAnotherNetwork
  }

  async restoreSession() {
    const provider = this.ethProvider ?? resolveMetaMaskProvider()

    this.address = null
    this.onAnotherNetwork = false

    if (!provider) return

    this.ethProvider = provider

    try {
      const chain = NeoXChainHelper.chainConfig(this.network)

      if (!(await this.isOnChain(provider, chain))) {
        this.onAnotherNetwork = true
        return
      }

      const accounts = await request<string[]>(provider, 'eth_accounts')

      this.address = accounts?.[0] ?? null
    } catch (error) {
      console.warn('[MetaMask] could not restore the session', error)
    }
  }

  private requireProvider(): MetaMaskEthereumProvider {
    const provider = this.ethProvider ?? resolveMetaMaskProvider()

    if (!provider) throw SdkNotInitializedError(WalletPlatform.METAMASK)

    this.ethProvider = provider

    return provider
  }

  private contextOf(
    provider: MetaMaskEthereumProvider
  ): MetaMaskProviderContext {
    return {type: 'metamask-ethereum', provider, address: this.address}
  }

  private async isOnChain(
    provider: MetaMaskEthereumProvider,
    chain: TNeoXChainConfig
  ): Promise<boolean> {
    const current = NeoXChainHelper.parseChainId(
      await request<string>(provider, 'eth_chainId')
    )

    return current === chain.chainId
  }

  private async switchToExpectedChain(provider: MetaMaskEthereumProvider) {
    const chain = NeoXChainHelper.chainConfig(this.network)

    if (await this.isOnChain(provider, chain)) return

    try {
      await request(provider, 'wallet_switchEthereumChain', [
        {chainId: chain.chainIdHex},
      ])
    } catch (error) {
      if (errorCodeOf(error) !== UNKNOWN_CHAIN_CODE) throw error

      await this.addChain(provider, chain)
    }

    if (!(await this.isOnChain(provider, chain))) {
      throw WrongMetaMaskNetworkError()
    }
  }

  private async addChain(
    provider: MetaMaskEthereumProvider,
    chain: TNeoXChainConfig
  ) {
    if (chain.rpcUrls.length === 0) throw WrongMetaMaskNetworkError()

    await request(provider, 'wallet_addEthereumChain', [
      {
        chainId: chain.chainIdHex,
        chainName: chain.chainName,
        rpcUrls: chain.rpcUrls,
        nativeCurrency: chain.nativeCurrency,
        ...(chain.blockExplorerUrls.length > 0
          ? {blockExplorerUrls: chain.blockExplorerUrls}
          : {}),
      },
    ])
  }

  private attachListeners(
    provider: MetaMaskEthereumProvider,
    onDeleteAdapter?: () => void
  ) {
    if (!provider.on) return

    const dropSession = () => {
      this.address = null
      onDeleteAdapter?.()
    }

    const onAccountsChanged = (...args: unknown[]) => {
      if (!this.address) return

      const accounts = args[0] as string[] | undefined
      const nextAddress = accounts?.[0] ?? null

      if (nextAddress?.toLowerCase() === this.address.toLowerCase()) return

      dropSession()
    }

    const onChainChanged = (...args: unknown[]) => {
      if (!this.address) return

      const chainId = NeoXChainHelper.parseChainId(args[0])

      if (chainId === NeoXChainHelper.chainId(this.network)) return

      this.onAnotherNetwork = true
      dropSession()
    }

    provider.on('accountsChanged', onAccountsChanged)
    provider.on('chainChanged', onChainChanged)

    this.detachListeners = () => {
      provider.removeListener?.('accountsChanged', onAccountsChanged)
      provider.removeListener?.('chainChanged', onChainChanged)
    }
  }
}
